import { startTransition, StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { HydratedRouter } from "react-router/dom";
import i18n from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import i18nConfig, { canHydrateDocument, documentNamespaces } from "~/i18n";
import { reloadOnHistoryTraversal } from "~/lib/translation-reload";
import { getInitialNamespaces } from "remix-i18next/client";
import { OFFLINE_DOCUMENT_NAMESPACES, offlineBootUrl, readOfflineDocument } from "~/lib/pwa/offline-fallback";
import { initSentryClient, tagOfflineFallbackBoot } from "~/lib/sentry.client";
import { clearOfflineRecoveryRecords } from "~/hooks/use-offline-recovery";

// T123 — initialise Sentry browser SDK before hydration so any error thrown
// during the React tree's first render is captured. The SDK installs the
// global `error` + `unhandledrejection` listeners as part of init.
initSentryClient();

async function hydrate() {
  // T841 — the service worker serves the offline document under the URL that failed. Boot
  // the router on the route that HTML was rendered for, carrying the requested URL in
  // `?from=` for the offline page's recovery; booting `location` as-is matched the URL's
  // route with no data for it (`No result found for routeId`, the route error screen).
  const offlineDocument = readOfflineDocument(document, window.location);
  // A normal document booted: any automatic recovery pending in this tab landed.
  if (!offlineDocument) clearOfflineRecoveryRecords();
  if (offlineDocument?.requested) {
    tagOfflineFallbackBoot(window.location.pathname);
    window.history.replaceState(
      window.history.state,
      "",
      offlineBootUrl(offlineDocument.offlinePath, offlineDocument.requested),
    );
  }
  // Until the router hydrates, nothing in this document answers Back/Forward: while the
  // dictionaries load (retries can take ~20 s offline) and, if they fail, for good (R-T956-6).
  // A traversal to another page loads that document instead of changing only the address.
  const stopFollowingHistory = reloadOnHistoryTraversal();
  // The offline document loads only its precached namespaces: waiting on every other
  // one (each retried with back-off, offline) kept it from hydrating while offline.
  const namespaces = offlineDocument
    ? [...OFFLINE_DOCUMENT_NAMESPACES]
    : documentNamespaces(window.location.pathname, getInitialNamespaces());

  const htmlLang = document.documentElement.lang || "es";
  const lng = htmlLang.toLowerCase().startsWith("en") ? "en" : "es";

  await i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      ...i18nConfig,
      ns: namespaces,
      lng,
      backend: {
        loadPath: "/locales/{{lng}}/{{ns}}.json",
      },
      detection: {
        order: ["path", "htmlTag", "cookie", "navigator"],
        lookupFromPathIndex: 0,
        caches: ["cookie"],
      },
    });

  await i18n.loadNamespaces(namespaces);

  // Keep the server-rendered public page and ordinary links/forms usable when dictionaries
  // fail. The existing Sentry unhandled-rejection listener records this; a reload retries.
  if (!canHydrateDocument(window.location.pathname, i18n, namespaces)) {
    throw new Error("Unable to load public document translations");
  }

  // The router takes Back/Forward over once it initialises, right after this hydration commits.
  stopFollowingHistory();
  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <I18nextProvider i18n={i18n}>
          <HydratedRouter />
        </I18nextProvider>
      </StrictMode>,
    );
  });
}

hydrate();
